























































































import AmetrasTextFieldTemplate from '@/fields/text/AmetrasTextFieldTemplate.vue';
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import BindAttrs from '@/utils/binds-attrs';
import AmetrasSimpleTextOutputTemplate from '@/fields/AmetrasSimpleTextOutputTemplate.vue';
import {cloneDeep, isEqual} from 'lodash';
import SelectDto = DisguiseDto.SelectDto;
import {FieldStyleContext} from '@/fields/field-style-context';
import {FIELD_EVENTS_INSTANCE, VueOnMapType} from '@/fields/field-events';

export interface SelectItem {
  text: string;
  value: string;
  ids: { [key: string]: string };
}

/**
 * General Component for Fields of type SELECT
 *
 * https://confluence.ametras.com/x/L4RQAw
 */
@Component({
  components: {AmetrasSimpleTextOutputTemplate, AmetrasTextFieldTemplate},
  mixins: [BindAttrs]
})
export default class AmetrasSelectFieldTemplate extends Vue {
  @Prop() marginDefinition!: string;
  @Prop() paddingDefinition!: string;
  @Prop() readonly!: boolean;
  @Prop() selectValues!: SelectDto[];
  @Prop() value!: string;
  @Prop() autoWidth!: boolean;
  @Prop({
    default: true
  }) required!: boolean;
  $refs!: {
    // unfortunately there is no exact type available.
    // See official doc: https://vuetifyjs.com/en/components/autocompletes
    autocomplete: Vue
  };
  /**
   * Indicates that we should render a simple mode of this component (increases the rendering performance)
   */
  @Prop() simple!: boolean;
  @Prop() allowEmptyValue!: boolean;
  /**
   * Indicates that all errors should be shown
   */
  @Prop() forceShowErrors!: boolean;
  /**
   * any external error present
   */
  @Prop() error!: boolean;

  @Prop() typeSpecificSettings!: DisguiseDto.SelectFieldSpecificSettings;
  @Prop() styleContext!: FieldStyleContext;
  internalItems: SelectItem[] = [];
  notAllowedValue!: string | undefined;
  backgroundColor: string = '';
  internalSelectValues: SelectDto[] = [];

  rules = [(currentValue: string) => {
    const validationMode = this.typeSpecificSettings && this.typeSpecificSettings.validationMode !== undefined
        ? this.typeSpecificSettings.validationMode
        : 'NONE';
    switch (validationMode) {
      case 'FULL':
        if (this.required && this.findCurrentSelectedValueRaw(currentValue) == null && (this.forceShowErrors || !this.readonly)) {
          return 'Bitte geben Sie einen Wert an!';
        }
      case 'EXTERNAL_ERRORS':
        const notHasAnyExternalError = typeof this.error === 'boolean' ? !this.error : true;
        return notHasAnyExternalError; // if any error from outside or any local error, return false
      default:
        return true;
    }
  }];

  // this is filtered pass through listener definition for readonly situation. if readonly, not all events should be passed tru
  passthroughReadonlyAllowedEvents: VueOnMapType =
      FIELD_EVENTS_INSTANCE.buildPassthroughVueOnMapForAllEvents((eventName, eventArgs) => {
        if (['input', 'change'].indexOf(eventName) < 0) {
          this.$emit(eventName, eventArgs);
        }
      });

  @Watch('selectValues') selectValuesChanged(newValue: SelectDto[], oldValue: SelectDto[]) {
    if (!this.disableFilteringIfNecessary()) {
      this.internalSelectValues = cloneDeep(newValue);
    }
  }

  @Watch('internalSelectValues') internalSelectValuesChanged(newValue: SelectDto[], oldValue: SelectDto[]) {
    if (!this.disableFilteringIfNecessary()) {
      if (!isEqual(newValue, oldValue)) {
        this.updateItems();
      }
    }
  }

  @Watch('value') valueChanged(newValue: string, oldValue: string) {
    if (newValue !== oldValue) {
      this.backgroundColor = this.value === this.notAllowedValue ? 'error' : '';
    }
  }

  updateSelectValues(newValues: SelectDto[]) {
    if (!this.disableFilteringIfNecessary()) {
      this.internalSelectValues = newValues;
    }
  }

  convertToSelectItems(
      selectValues: SelectDto[] = []
  ): SelectItem[] {
    if (!this.disableFilteringIfNecessary()) {
      this.notAllowedValue = undefined;
      const selectItems: SelectItem[] = [];
      let id: number = 0;
      let currentlySelectedValueIsPossible = false;
      for (const currentId of selectValues || []) {
        selectItems[id] = {
          text: currentId.text || '',
          value: currentId.value || '',
          ids: currentId.ids || {}
        };
        if (this.value === currentId.value) {
          currentlySelectedValueIsPossible = true;
        }
        id++;
      }

      if (!currentlySelectedValueIsPossible) {
        this.notAllowedValue = this.value;
        selectItems[id] = {
          text: this.allowEmptyValue || (this.typeSpecificSettings && this.typeSpecificSettings.allowEmptyValue) ? '' : 'Unbekannt (' + this.value + ')',
          value: this.value,
          ids: {}
        };
      }
      return selectItems;
    }
    return [];
  }

  changeEvent(event: any) {
    if (!(this.allowEmptyValue || (this.typeSpecificSettings && this.typeSpecificSettings.allowEmptyValue))) {
      this.backgroundColor = this.notAllowedValue === event ? 'error' : '';
    }
  }

  created() {
    if (!this.disableFilteringIfNecessary()) {
      this.init();
    }
  }

  /**
   * Finds the current selected value for readonly mode
   *
   * id comparison is missing.
   */
  findCurrentSelectedValue() {
    if (this.typeSpecificSettings &&
        this.typeSpecificSettings.allowCustomUserInput) {
      return this.value;
    }

    if (!this.disableFilteringIfNecessary()) {
      let result = '';
      if (this.internalItems) {
        for (const currentSelectValue of this.internalItems) {
          if (currentSelectValue.value === this.value) {
            result = currentSelectValue.text || '';
            break;
          }
        }
      }
      return result;
    }
    return this.value;
  }

  /**
   * Finds the current selected value for readonly mode
   *
   * id comparison is missing.
   */
  findCurrentSelectedValueRaw(value: string) {
    if (!this.disableFilteringIfNecessary()) {
      let result = null;
      for (const currentSelectValue of this.internalSelectValues || []) {
        if (currentSelectValue.value === value) {
          result = currentSelectValue;
          break;
        }
      }
      return result;
    }
    return value;
  }

  captureEnterKey(event: Event) {
    const isAnySelectBoxOpened = document.querySelectorAll('.v-select--is-menu-active').length !== 0;
    if (isAnySelectBoxOpened) {
      event.stopPropagation();
    } else {
      event.stopImmediatePropagation();
    }
  }

  /**
   * Opens the menu and sets the focus to the menu
   */
  openSelectMenuAndFocus() {
    if (this.typeSpecificSettings && this.typeSpecificSettings.allowCustomUserInput) {
      (this.$refs.autocomplete as any).isMenuActive = true;
      (this.$refs.autocomplete as any).isFocused = true;
    } else {
      (this.$refs.autocomplete as any).activateMenu();
      (this.$refs.autocomplete as any).isFocused = true;
    }
  }

  /**
   * Opens the menu
   */
  openSelectMenu() {
    if (!this.typeSpecificSettings || this.typeSpecificSettings.activateMenuOnFocus) {
      if (this.typeSpecificSettings && this.typeSpecificSettings.allowCustomUserInput) {
        (this.$refs.autocomplete as any).isMenuActive = true;
      } else {
        (this.$refs.autocomplete as any).activateMenu();
      }
    }
  }

  updateItems() {
    if (!this.disableFilteringIfNecessary()) {
      if (this.internalSelectValues) {
        this.internalItems = this.convertToSelectItems(this.internalSelectValues);
      }
    }
    this.backgroundColor = this.value === this.notAllowedValue ? 'error' : '';
  }

  private init() {
    if (this.selectValues) {
      this.internalSelectValues = this.selectValues;
    }
  }

  private disableFilteringIfNecessary() {
    if (!this.readonly) {
      return false;
    }

    return this.typeSpecificSettings && this.typeSpecificSettings.disableReadonlyFiltering;
  }

}
